import React, { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useRecoilState } from "recoil";
import CancelButton from "../../../../../components/CancelButton";
import ConfirmBoxModal from "../../../../../components/ConfirmBoxModal";
import SubmitHookForm from "../../../../../components/SubmitHookForm";
import {
  addStatePaymentRemittanceType,
  editStatePaymentRemittanceType,
  rowForEditPaymentRemittanceType,
  paymentRemittanceTypeSaving,
} from "../atoms/atoms";
import useUpdatePaymentRemittanceType from "../hooks/useUpdatePaymentRemittanceType";
import useAddPaymentRemittanceType from "../hooks/useAddPaymentRemittanceType";
import { PaymentRemittanceTypeInfo } from "../types";
import PaymentRemittanceTypeDetails from "./PaymentRemittanceTypeDetails";
import Timer from "../../Timer";

export default function PaymentRemittanceTypeForm(props: {
  refetch: () => void;
}) {
  // Atom States
  const [isAddState, setIsAddState] = useRecoilState(
    addStatePaymentRemittanceType
  );
  const [isEditState, setIsEditState] = useRecoilState(
    editStatePaymentRemittanceType
  );
  const [isSaving, setIsSaving] = useRecoilState(paymentRemittanceTypeSaving);
  const [editData, setEditData] = useRecoilState(
    rowForEditPaymentRemittanceType
  );

  // Local States
  const { refetch } = props;
  const [newData, setNewData] = useState<PaymentRemittanceTypeInfo>();
  const [saveModal, setSaveModal] = useState(false);
  const [changes, setChanges] = useState(false);

  // Hook Functions
  const updatePaymentRemittanceType = useUpdatePaymentRemittanceType();
  const addPaymentRemittanceType = useAddPaymentRemittanceType();

  // Form Functions
  const useFormMethods = useForm({
    defaultValues: { ...editData },
  });

  const onSubmit = (data) => {
    const value = {
      ...data,
      paymentRemittanceTypeName: data.paymentRemittanceTypeName?.trim(),
    };
    setNewData(value);
    setSaveModal(true);
    setIsSaving(true);
  };

  const handleCancel = () => {
    useFormMethods.reset();
    useFormMethods.clearErrors();
    setEditData(undefined);
    setIsAddState(false);
    setIsEditState(false);
  };

  const handleSave = async () => {
    if (newData) {
      if (isEditState) {
        updatePaymentRemittanceType(newData);
      } else {
        addPaymentRemittanceType(newData);
      }
    }
    await Timer();
    setIsSaving(false);
    useFormMethods.reset();
    useFormMethods.clearErrors();
    setEditData(undefined);
    setIsAddState(false);
    setIsEditState(false);
    refetch();
  };

  return (
    <>
      <div className="px-2 py-2 space-y-2 border-l-2 border-primary box-shadow">
        <FormProvider {...useFormMethods}>
          <form onSubmit={useFormMethods.handleSubmit(onSubmit)}>
            <div className="flex flex-row  justify-between items-center text-quinary font-bold font-sans text-lg pb-1">
              <div className="ml-2 font-bold text-primary text-base">
                {isAddState
                  ? "Add New Payment Remittance Type"
                  : "Edit Payment Remittance Type"}
              </div>
              <div className="flex justify-between gap-2 mr-2">
                <SubmitHookForm
                  type="submit"
                  disabled={!changes}
                  buttonText={isSaving ? "Saving.." : "Save"}
                  className={undefined}
                  onClick={undefined}
                />
                <CancelButton
                  onClick={(event) => {
                    event.preventDefault();
                    handleCancel();
                  }}
                />
              </div>
            </div>
            <div className="mx-2 border-b-2 border-secondary"></div>
            <div className="text-xs text-secondary italic font-normal mt-2 ml-2">
              <b> Note:</b> All fields with * are required to fill.
            </div>
          </form>
          <div>
            <PaymentRemittanceTypeDetails
              data={editData}
              setChanges={setChanges}
            />
          </div>
        </FormProvider>
      </div>

      {saveModal && (
        <ConfirmBoxModal
          ModalTitle={
            isAddState
              ? "Add Payment Remittance Type"
              : "Update Payment Remittance Type"
          }
          qConfirmation={`Are you sure you want to ${
            isAddState ? "add " : "update "
          } this?`}
          isOpen={saveModal}
          onClose={() => {
            setSaveModal(false);
            setIsSaving(false);
          }}
          onClickFunction={() => {
            setSaveModal(false);
            setIsSaving(false);
            handleSave();
          }}
          cancelButtonRef={undefined}
        />
      )}
    </>
  );
}
